import React from 'react';
import { useTranslation } from 'react-i18next';

// Material-UI Components
import { makeStyles } from 'tss-react/mui';
import { Grid, Typography, useMediaQuery, Button, useTheme } from '@mui/material';

// Custom Hooks & Store
import useStore from '../../Store/Store';

// Local Components
import styles from '../../Main/main.styles';
import NewUserInfo from '../../Main/NewUserInfo';
import RecipientsSelector from '../Components/RecipientsSelector';
import HealthCard from './components/HealthCard';
import LeftSideVitalsTabs from './components/LeftSideVitalsTabs';
import MobilityGraph from './components/MobilityGraph';
import ActivityGraph from './components/ActivityGraph';
import VitalsGraph from './components/VitalsGraph';
import GoalModal from './components/GoalModal';

// Styles
import './healthoverview.css';

// images
import target from '../../images/Target.png';
import pencil from '../../images/Pencil.png';

// constants
import { TAB_MENU, VITALS_TAB_MENU, ACTIVITIES_TAB_MENU } from '../../constants';
import DailyProgressBar from './components/DailyProgressBar';
import SideBar from '../../components/SideBar';
import PageHeader from '../../components/PageHeader';
const useStyles = makeStyles()(styles);

const HealthOverviewContent = () => {
  // Component state
  const [activeTab, setActiveTab] = React.useState(TAB_MENU[0]);
  const [activeLeftTabMenu, setActiveLeftTabMenu] = React.useState(VITALS_TAB_MENU[0]);
  const [isGoalModalOpen, setIsGoalModalOpen] = React.useState(false);
  const [dailyGoal, setDailyGoal] = React.useState(10000);

  // Get store values
  const width = useStore((state) => state.screenWidth);
  const user = useStore((state) => state.user);
  const selectedRecipient = useStore((state) => state.selectedRecipient);
  // Theme for responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // translation

  const { t } = useTranslation();
  const TAB_LABEL_KEYS = {
    Vitals: 'vitals',
    Activities: 'activities',
    'Fall detection': 'fallDetection',
    'Gait Speed': 'gaitSpeed',
    'Symptoms & Conditions': 'symptomsAndConditions',
  };

  const VITAL_LABEL_KEYS = {
    'Heart Rate': 'heartRate',
    'Blood Pressure': 'bloodPressure',
    Calories: 'calories',
    Glucose: 'glucose',
  };

  const ACTIVITY_LABEL_KEYS = {
    Distance: 'distance',
    Steps: 'steps',
    Sleep: 'sleep',
  };

  const translateLabel = (name) => {
    return t(VITAL_LABEL_KEYS[name] || ACTIVITY_LABEL_KEYS[name] || name);
  };

  // Handlers
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === 'Vitals') {
      setActiveLeftTabMenu(VITALS_TAB_MENU[0]);
    } else if (tab === 'Activities') {
      setActiveLeftTabMenu(ACTIVITIES_TAB_MENU[0].name);
    }
  };

  const isTabEnabled = (tabName) => {
    // Add check for Activities tab items
    if (ACTIVITIES_TAB_MENU.some((activity) => activity.name === tabName)) {
      return true;
    }
    return ['Heart Rate', 'Blood Pressure', 'Calories', 'Glucose'].includes(tabName);
  };

  const handleLeftTabClick = (tab) => {
    if (isTabEnabled(tab)) {
      setActiveLeftTabMenu(tab);
    }
  };

  const handleGoalSave = (newGoal) => {
    setDailyGoal(newGoal);
    // Here you can add API call to save the goal to the backend
    console.log('New goal saved:', newGoal);
  };

  // Determine which left tab menu to show based on active tab
  const currentLeftTabMenu =
    activeTab === 'Activities' ? ACTIVITIES_TAB_MENU.map((item) => item.name) : VITALS_TAB_MENU;

  // Check if we should show the left side tabs
  const showLeftSideTabs = activeTab !== 'Fall detection' && activeTab !== 'Gait Speed';

  // Determine which chart component to render based on active tab
  const renderChartComponent = () => {
    if (activeTab === 'Gait Speed' || activeTab === '') {
      return <MobilityGraph userData={user} recipientId={selectedRecipient} type={activeTab} />;
    } else if (activeTab === 'Fall detection') {
      return <MobilityGraph userData={user} recipientId={selectedRecipient} type={activeTab} />;
    } else if (activeTab === 'Activities') {
      return <ActivityGraph userData={user} recipientId={selectedRecipient} type={activeLeftTabMenu} />;
    } else if (activeTab === 'Vitals') {
      return <VitalsGraph userData={user} recipientId={selectedRecipient} type={activeLeftTabMenu} />;
    }
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div>
          <SideBar />
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <NewUserInfo isSideBar={false} />
          <div style={{ padding: '20px' }}>
            <PageHeader title={t('healthOverview')} />

            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '20px',
              }}
            >
              <Grid
                container
                direction="column"
                style={{
                  // width: isMobile ? '100%' : '100%',
                  marginBottom: isMobile ? '20px' : '0',
                }}
              >
                <HealthCard />
                <Grid
                  container
                  style={{
                    marginTop: '10px',
                    // width: '95%',
                  }}
                >
                  <Grid
                    container
                    className="tab-menu"
                    sx={{
                      overflowX: isMobile ? 'auto' : 'visible',
                      flexWrap: isMobile ? 'nowrap' : 'wrap',
                    }}
                  >
                    {TAB_MENU.map((item, idx) => (
                      <Grid
                        key={idx}
                        className="tab-menu-item"
                        style={{
                          background: activeTab === item ? '#f5f5f5' : 'inherit',
                          borderRadius: activeTab === item ? '40px' : '0',
                          minWidth: isMobile ? '120px' : 'auto',
                          opacity: item === 'Symptoms & Conditions' ? 0.5 : 1,
                          cursor: item === 'Symptoms & Conditions' ? 'not-allowed' : 'pointer',
                        }}
                        onClick={() => item !== 'Symptoms & Conditions' && handleTabClick(item)}
                      >
                        {t(TAB_LABEL_KEYS[item] || item)}
                      </Grid>
                    ))}
                  </Grid>
                  <Grid container spacing={2}>
                    {showLeftSideTabs ? (
                      // Regular layout with left side tabs
                      <>
                        <Grid item xs={12} md={3}>
                          <Grid
                            container
                            style={{
                              background: '#fff',
                              padding: '20px',
                              borderRadius: '8px',
                              height: '100%',
                              boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
                              marginBottom: isMobile ? '20px' : '0',
                            }}
                          >
                            <LeftSideVitalsTabs
                              handleLeftTabClick={handleLeftTabClick}
                              leftTabMenu={currentLeftTabMenu}
                              activeTab={activeLeftTabMenu}
                              isActivitiesTab={activeTab === 'Activities'}
                              activitiesData={activeTab === 'Activities' ? ACTIVITIES_TAB_MENU : null}
                              isTabEnabled={isTabEnabled}
                              userData={user}
                              recipientId={selectedRecipient}
                            />
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <Grid
                            container
                            style={{
                              background: '#fff',
                              padding: '20px',
                              borderRadius: '8px',
                              height: '100%',
                              boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
                            }}
                          >
                            <Typography
                              variant={isMobile ? 'h5' : 'h4'}
                              component="h1"
                              className="title"
                              style={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                gap: isMobile ? '10px' : '0',
                              }}
                            >
                              {activeTab === 'Gait Speed' ? t('gaitSpeed') : translateLabel(activeLeftTabMenu)}
                              {/* {activeTab === 'Activities' && (
                                <>
                                  <span
                                    style={{
                                      marginLeft: isMobile ? '0' : '20px',
                                      marginTop: isMobile ? '10px' : '2px',
                                      width: isMobile ? '100%' : 'auto',
                                    }}
                                  >
                                    <DailyProgressBar progress={8500} max={dailyGoal} color="#006E5F" />
                                  </span>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<img src={target} alt="Target" style={{ width: '20px', height: '20px' }} />}
                                    endIcon={<img src={pencil} alt="Target" style={{ width: '20px', height: '20px' }} />}
                                    sx={{
                                      marginLeft: isMobile ? '0' : 'auto',
                                      marginTop: isMobile ? '10px' : '0',
                                      borderRadius: '24px',
                                      backgroundColor: '#3b9b8f',
                                      width: isMobile ? '100%' : 'auto',
                                      '&:hover': {
                                        backgroundColor: '#2d7a70',
                                      },
                                    }}
                                    onClick={() => setIsGoalModalOpen(true)}
                                  >
                                    Daily goal {dailyGoal / 1000}K
                                  </Button>
                                </>
                              )} */}
                            </Typography>

                            {renderChartComponent()}
                          </Grid>
                        </Grid>
                      </>
                    ) : (
                      // Full width chart for Fall detection
                      <Grid item xs={12}>
                        <Grid
                          container
                          style={{
                            background: '#fff',
                            padding: '20px',
                            borderRadius: '8px',
                            height: '100%',
                            boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
                          }}
                        >
                          <div style={{}}>
                            <Typography variant="h4" component="h1" className="title" style={{ marginBottom: '30px' }}>
                              {activeTab}
                            </Typography>
                            {renderChartComponent()}
                          </div>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </div>

          {/* Goal Modal */}
          <GoalModal
            open={isGoalModalOpen}
            handleClose={() => setIsGoalModalOpen(false)}
            currentGoal={dailyGoal}
            onSave={handleGoalSave}
            type={activeLeftTabMenu}
          />
        </div>
      </div>
    </>
  );
};

// The main component that prevents infinite loops
const HealthOverview = () => {
  return <HealthOverviewContent />;
};

export default HealthOverview;
